import { useRef, useState } from "react";
import { ImagePlus, Loader2, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, imgSrc } from "@/lib/api";
import { uploadImage } from "@/lib/imageUpload";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const MIN_CHARS = 30, MAX = 4;

export function PostEditDialog({ post, onClose, onSaved }) {
  const { user } = useAuth();
  const [text, setText] = useState(post.text);
  const [images, setImages] = useState(post.images);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const add = async (files) => {
    setUploading(true);
    try { for (const f of [...files].slice(0, MAX - images.length)) { const { url } = await uploadImage("/feed/upload", f); setImages((l) => [...l, url]); } }
    catch (e) { toast.error(formatErr(e)); } finally { setUploading(false); }
  };
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/feed/posts/${post.id}`, { text, images }); onSaved(data); toast.success("Beitrag aktualisiert ✨"); onClose(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const short = text.trim().length < MIN_CHARS;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-lg" data-testid="post-edit-dialog">
        <DialogTitle className="font-display text-xl font-extrabold">Beitrag bearbeiten</DialogTitle>
        <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={user?.role === "admin" ? 5000 : 2000} rows={5} data-testid="post-edit-input"
          className="w-full rounded-2xl border border-pink-200 p-3 text-base outline-none focus:border-rose-400 resize-none" />
        {short && <p className="text-xs font-semibold text-amber-600" data-testid="post-edit-min-hint">Mindestens {MIN_CHARS} Zeichen ({text.trim().length}/{MIN_CHARS})</p>}
        {images.length > 0 && (
          <div className="grid grid-cols-4 gap-2" data-testid="post-edit-images">
            {images.map((u) => (
              <div key={u} className="relative aspect-square rounded-xl overflow-hidden bg-rose-50">
                <img src={imgSrc(u)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setImages((l) => l.filter((x) => x !== u))} data-testid="post-edit-remove-image" className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || images.length >= MAX} data-testid="post-edit-add-image"
            className="inline-flex items-center gap-2 px-3 h-10 rounded-full text-sm font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-40 mr-auto">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-5 h-5" />} Bild ({images.length}/{MAX})
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="post-edit-file-input" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
          <button type="button" onClick={onClose} data-testid="post-edit-cancel" className="h-10 px-4 rounded-full bg-slate-100 text-sm font-bold text-slate-600">Abbrechen</button>
          <button type="button" onClick={save} disabled={busy || uploading || short} data-testid="post-edit-save"
            className="h-10 px-5 rounded-full pink-gradient text-white text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Speichern
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
